import type { Priority, Mood, DeadlinePreset, Card, Category } from "../types";

export const priorityMeta: Record<Priority, { label: string; color: string; ring: string; glow: string }> = {
  low: { label: "Baixa", color: "text-slate-300", ring: "ring-slate-500/40", glow: "bg-slate-500/20" },
  medium: { label: "Média", color: "text-sky-300", ring: "ring-sky-500/40", glow: "bg-sky-500/20" },
  high: { label: "Alta", color: "text-amber-300", ring: "ring-amber-500/40", glow: "bg-amber-500/20" },
  urgent: { label: "Urgente", color: "text-rose-300", ring: "ring-rose-500/50", glow: "bg-rose-500/25" },
};

export const moodMeta: Record<Mood, { label: string; border: string; bg: string; text: string }> = {
  urgent: { label: "Urgente", border: "#EF4444", bg: "rgba(239,68,68,0.12)", text: "text-rose-200" },
  exploratory: { label: "Exploratória", border: "#00D9FF", bg: "rgba(0,217,255,0.10)", text: "text-cyan-200" },
  routine: { label: "Rotina", border: "#64748B", bg: "rgba(100,116,139,0.12)", text: "text-slate-200" },
  creative: { label: "Criativa", border: "#A855F7", bg: "rgba(168,85,247,0.12)", text: "text-violet-200" },
};

export function categoryBg(hex: string): string {
  // converte #RRGGBB para hsla com alfa leve para bg do chip
  const c = hex.replace("#", "");
  if (c.length !== 6) return "rgba(100,116,139,0.18)";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},0.18)`;
}

export function categoryBorder(hex: string): string {
  const c = hex.replace("#", "");
  if (c.length !== 6) return "rgba(100,116,139,0.45)";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},0.5)`;
}

export const CATEGORY_COLOR_PALETTE = [
  "#38bdf8", "#a855f7", "#10b981", "#f59e0b", "#ef4444",
  "#ec4899", "#6366f1", "#14b8a6", "#f97316", "#84cc16",
  "#64748b", "#0ea5e9", "#8b5cf6", "#22c55e", "#eab308",
];

function parseDueDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return parseDueDate(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

export function fmtRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins} min atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d atrás`;
  return d.toLocaleDateString("pt-BR");
}

export function isOverdue(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const due = parseDueDate(iso);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

export function matchDeadlinePreset(iso: string | null, preset: DeadlinePreset): boolean {
  if (preset === "no_date") return !iso;
  if (!iso) return false;
  const startOfDue = parseDueDate(iso);
  startOfDue.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((startOfDue.getTime() - today.getTime()) / 86400000);
  if (preset === "today") return diff === 0;
  if (preset === "this_week") return diff >= 0 && diff <= 7;
  if (preset === "overdue") return diff < 0;
  return false;
}

export function sortCards(cards: Card[], key: string): Card[] {
  const arr = [...cards];
  switch (key) {
    case "deadline":
      return arr.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      });
    case "effort": {
      const order = ["Complexa", "Difícil", "Regular", "Fácil", "Trivial"];
      return arr.sort((a, b) => order.indexOf(b.effort || "") - order.indexOf(a.effort || ""));
    }
    case "created":
      return arr.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    case "manual":
    default:
      return arr.sort((a, b) => a.position - b.position);
  }
}

export function exportAsMarkdown(
  lanes: { id: number; title: string }[],
  cards: Card[],
  categories: Category[],
): string {
  const catById = new Map(categories.map((c) => [c.id, c]));
  const byLane = new Map<number, Card[]>();
  for (const c of cards) {
    if (!byLane.has(c.laneId)) byLane.set(c.laneId, []);
    byLane.get(c.laneId)!.push(c);
  }
  const lines: string[] = [`# on_board · export ${new Date().toLocaleString("pt-BR")}`, ""];
  for (const l of lanes) {
    const list = (byLane.get(l.id) || []).sort((a, b) => a.position - b.position);
    lines.push(`## ${l.title} (${list.length})`);
    for (const c of list) {
      lines.push(`- **[${c.id}] ${c.title}** \`${c.priority}\` · mood: ${c.mood}${c.effort ? ` · ${c.effort}` : ""}${c.dueDate ? ` · due ${c.dueDate}` : ""}`);
      const catNames = (c.categoryIds || []).map((id) => catById.get(id)?.name).filter(Boolean);
      if (catNames.length) lines.push(`  - categorias: ${catNames.map((t) => `\`${t}\``).join(", ")}`);
      if (c.whyItMatters) lines.push(`  - *${c.whyItMatters}*`);
      if (c.description) lines.push(`  - ${c.description.split("\n")[0].slice(0, 200)}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

export function exportAsCSV(cards: Card[], categories: Category[]): string {
  const catById = new Map(categories.map((c) => [c.id, c]));
  const headers = [
    "id", "title", "laneId", "priority", "mood",
    "effort", "dueDate", "categories", "whyItMatters", "createdAt", "updatedAt",
  ];
  const rows = cards.map((c) => {
    const catNames = (c.categoryIds || []).map((id) => catById.get(id)?.name).filter(Boolean);
    return [
      c.id,
      JSON.stringify(c.title),
      c.laneId,
      c.priority,
      c.mood,
      c.effort || "",
      c.dueDate ?? "",
      `"${catNames.join("|")}"`,
      JSON.stringify(c.whyItMatters ?? ""),
      c.createdAt,
      c.updatedAt,
    ].join(",");
  });
  return [headers.join(","), ...rows].join("\n");
}

export function downloadFile(filename: string, content: string, type = "text/plain"): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
